import { computed, reactive, ref } from 'vue'
import axios from 'axios'
import appApi, {
  apiErrorMessage,
  apiRetryAfterSec,
  clearTokens,
  getAccessToken,
  getRefreshToken,
  getSessionEpoch,
  invalidateSession,
  refreshAccessToken,
  saveTokens,
  setOnSessionCleared,
} from '@/services/appApi'
import { notifyNativeLogout } from '@/utils/nativeBridge'
import { markAppClientVersion, syncAppClientVersion } from '@/utils/appClientVersion'
import { isFnaFlavor } from '@/config/appFlavor'

export interface AppUser {
  appRole: string
  name?: string
  email?: string
  mCode?: string
  centerName?: string
  loginId?: string
  phone?: string
  addr?: string
  birthDate?: string
  /** 주민번호 앞자리 (요양보호사) */
  ssnFront?: string
  /** 마스킹된 주민번호 (예: 900101-1******) */
  ssnMasked?: string
  hasSsn?: boolean
  bizGubun?: string
  longtermCode?: string
  /** 사회복지사가 보는 소속 기관 정보 */
  centerLoginId?: string
  centerBizGubun?: string
  centerLongtermCode?: string
  centerBossName?: string
  centerBossPhone?: string
  centerBossEmail?: string
  centerAddr?: string
  orgName?: string
  cayIdx?: number
  casIdx?: number
  /** 웹 회계담당 u_idx */
  uIdx?: number
  isAdmin?: boolean
  /** 슈퍼 센터 계정 (id super) */
  isSuper?: boolean
  hasPhoto?: boolean
  photoUpdatedAt?: string | null
  /** 기관도장(직인) — 센터장 */
  hasSeal?: boolean
  sealUpdatedAt?: string | null
  /** 웹 회계담당자(ACCOUNT) 배정 여부 — 시설 단위 */
  hasAccountAssignee?: boolean
  /** 사회복지사: 센터장이 부여한 앱 회계관리 권한 */
  canAccounting?: boolean
  /** 사회복지사: 웹 사원 매칭 여부 */
  matched?: boolean
  /** 센터장: 웹 사원(시설장) 매칭 정보 — 없으면 null */
  employeeMatch?: {
    clciIdx: number
    clciSource: string
    name?: string | null
    birthDate?: string | null
    jobType?: string | null
    phone?: string | null
    email?: string | null
    addr?: string | null
    workStartDate?: string | null
  } | null
}

export interface AccountantMember {
  mCode: string
  name: string
  bizGubun?: string
  bizGubunName?: string
  longtermCode?: string
  orgName?: string | null
}

interface AuthState {
  user: AppUser | null
  booting: boolean
  affiliations: Record<string, any>[]
  joinRequests: Record<string, any>[]
  affiliationCount: number
  accountantMembers: AccountantMember[]
}

const SUPER_RETURN_KEY = 'caion_super_return'

type SuperReturn = { access: string; refresh: string }

function readSuperReturn(): SuperReturn | null {
  try {
    const raw = localStorage.getItem(SUPER_RETURN_KEY)
    if (!raw) return null
    const parsed = JSON.parse(raw) as SuperReturn
    if (!parsed?.access || !parsed?.refresh) return null
    return parsed
  } catch {
    return null
  }
}

function writeSuperReturn(access: string, refresh: string) {
  localStorage.setItem(SUPER_RETURN_KEY, JSON.stringify({ access, refresh }))
  impersonating.value = true
}

function clearSuperReturn() {
  localStorage.removeItem(SUPER_RETURN_KEY)
  impersonating.value = false
}

const impersonating = ref(!!readSuperReturn())

async function refreshSavedSuper(): Promise<string | null> {
  const saved = readSuperReturn()
  if (!saved?.refresh) return null
  try {
    const { data } = await axios.post(
      '/api/app/auth/refresh',
      { refreshToken: saved.refresh },
      { timeout: 15000, headers: { 'Content-Type': 'application/json' } }
    )
    const session = data?.data as Record<string, any> | undefined
    if (!data?.success || !session?.accessToken) return null
    const access = String(session.accessToken)
    const refresh = String(session.refreshToken || saved.refresh)
    writeSuperReturn(access, refresh)
    return access
  } catch {
    return null
  }
}

const state = reactive<AuthState>({
  user: null,
  booting: true,
  affiliations: [],
  joinRequests: [],
  affiliationCount: 0,
  accountantMembers: [],
})

function mapAccountantMembers(list: unknown): AccountantMember[] {
  if (!Array.isArray(list)) return []
  return list.map((raw) => {
    const m = raw as Record<string, any>
    return {
      mCode: String(m.mCode || m.m_code || ''),
      name: String(m.name || m.m_name || ''),
      bizGubun: m.bizGubun != null ? String(m.bizGubun) : undefined,
      bizGubunName: m.bizGubunName != null ? String(m.bizGubunName) : undefined,
      longtermCode: m.longtermCode != null ? String(m.longtermCode) : undefined,
      orgName: m.orgName != null ? String(m.orgName) : null,
    }
  }).filter((m) => m.mCode)
}

function mapUser(m: Record<string, any>): AppUser {
  return {
    appRole: String(m.appRole || ''),
    name: m.name != null ? String(m.name) : undefined,
    email: m.email != null ? String(m.email) : undefined,
    mCode: m.mCode != null ? String(m.mCode) : undefined,
    centerName: m.centerName != null ? String(m.centerName) : undefined,
    loginId: m.loginId != null ? String(m.loginId) : undefined,
    phone: m.phone != null ? String(m.phone) : undefined,
    addr: m.addr != null ? String(m.addr) : undefined,
    birthDate: m.birthDate != null ? String(m.birthDate).slice(0, 10) : undefined,
    ssnFront: m.ssnFront != null ? String(m.ssnFront) : undefined,
    ssnMasked: m.ssnMasked != null ? String(m.ssnMasked) : undefined,
    hasSsn: m.hasSsn === true || m.hasSsn === 'Y',
    bizGubun: m.bizGubun != null ? String(m.bizGubun) : undefined,
    longtermCode: m.longtermCode != null ? String(m.longtermCode) : undefined,
    centerLoginId: m.centerLoginId != null ? String(m.centerLoginId) : undefined,
    centerBizGubun: m.centerBizGubun != null ? String(m.centerBizGubun) : undefined,
    centerLongtermCode:
      m.centerLongtermCode != null ? String(m.centerLongtermCode) : undefined,
    centerBossName: m.centerBossName != null ? String(m.centerBossName) : undefined,
    centerBossPhone: m.centerBossPhone != null ? String(m.centerBossPhone) : undefined,
    centerBossEmail: m.centerBossEmail != null ? String(m.centerBossEmail) : undefined,
    centerAddr: m.centerAddr != null ? String(m.centerAddr) : undefined,
    orgName: m.orgName != null ? String(m.orgName) : undefined,
    cayIdx: m.cayIdx != null ? Number(m.cayIdx) : undefined,
    casIdx: m.casIdx != null ? Number(m.casIdx) : undefined,
    uIdx: m.uIdx != null ? Number(m.uIdx) : undefined,
    isAdmin: m.isAdmin === true || m.isAdmin === 'Y',
    isSuper: m.isSuper === true,
    hasPhoto: m.hasPhoto === true || m.hasPhoto === 'Y' || Boolean(m.cay_photo),
    photoUpdatedAt: m.photoUpdatedAt != null ? String(m.photoUpdatedAt) : null,
    hasSeal: m.hasSeal === true || m.hasSeal === 'Y' || Boolean(m.m_seal_photo),
    sealUpdatedAt: m.sealUpdatedAt != null ? String(m.sealUpdatedAt) : null,
    hasAccountAssignee: m.hasAccountAssignee === true || m.hasAccountAssignee === 'Y',
    canAccounting: m.canAccounting === true || m.canAccounting === 'Y',
    matched: m.matched === true || m.matched === 'Y',
    employeeMatch:
      m.employeeMatch && typeof m.employeeMatch === 'object'
        ? (m.employeeMatch as AppUser['employeeMatch'])
        : null,
  }
}

function applyUserPatch(m: Record<string, any>) {
  state.user = mapUser(m)
}

async function applySession(data: Record<string, any>, epoch?: number) {
  if (epoch != null && epoch !== getSessionEpoch()) return
  const access = String(data.accessToken || '')
  const refresh = String(data.refreshToken || '')
  saveTokens(access, refresh)
  markAppClientVersion()
  state.user = mapUser(data.user || {})
  if (data.members) {
    state.accountantMembers = mapAccountantMembers(data.members)
  }
  state.booting = false
}

async function afterAuthSession() {
  const { syncPushAfterAuth } = await import('@/composables/useSchedulePush')
  void syncPushAfterAuth()
}

async function refreshMe() {
  const epoch = getSessionEpoch()
  try {
    const res = await appApi.get('/me')
    if (epoch !== getSessionEpoch()) return
    if (!res.success || !res.data) return
    const data = res.data as Record<string, any>
    state.user = mapUser(data.user || {})
    state.affiliations = Array.isArray(data.affiliations) ? data.affiliations : []
    state.joinRequests = Array.isArray(data.joinRequests) ? data.joinRequests : []
    state.affiliationCount =
      typeof data.affiliationCount === 'number' ? data.affiliationCount : state.affiliations.length
    if (data.members) {
      state.accountantMembers = mapAccountantMembers(data.members)
    }
    state.booting = false
  } catch {
    /* ignore */
  }
}

async function fetchProfile(opts?: { signal?: AbortSignal }) {
  const epoch = getSessionEpoch()
  const res = await appApi.get('/me', undefined, opts)
  if (epoch !== getSessionEpoch()) throw new Error('세션이 종료되었습니다.')
  if (!res.success || !res.data) throw new Error(res.message || '조회 실패')
  const data = res.data as Record<string, any>
  state.user = mapUser(data.user || {})
  state.affiliations = Array.isArray(data.affiliations) ? data.affiliations : []
  state.joinRequests = Array.isArray(data.joinRequests) ? data.joinRequests : []
  state.affiliationCount =
    typeof data.affiliationCount === 'number' ? data.affiliationCount : state.affiliations.length
  if (data.members) {
    state.accountantMembers = mapAccountantMembers(data.members)
  }
  return data
}

async function updateProfile(payload: {
  name?: string
  phone?: string
  addr?: string
  birthDate?: string
  email?: string
  ssnFront?: string
  ssnBack?: string
}) {
  try {
    const res = await appApi.put('/me', payload)
    if (!res.success) throw new Error(res.message || '저장 실패')
    const data = res.data as Record<string, any> | undefined
    if (data?.user) state.user = mapUser(data.user)
  } catch (e) {
    throw new Error(apiErrorMessage(e, '저장 실패'))
  }
}

async function changePassword(currentPassword: string, newPassword: string) {
  try {
    const res = await appApi.post('/me/password', { currentPassword, newPassword })
    if (!res.success) throw new Error(res.message || '변경 실패')
  } catch (e) {
    throw new Error(apiErrorMessage(e, '변경 실패'))
  }
}

let bootPromise: Promise<void> | null = null

function clearLocalAuth() {
  invalidateSession()
  clearTokens()
  clearSuperReturn()
  state.user = null
  state.affiliations = []
  state.joinRequests = []
  state.affiliationCount = 0
  state.accountantMembers = []
  state.booting = false
  bootPromise = null
}

setOnSessionCleared(() => {
  state.user = null
  state.affiliations = []
  state.joinRequests = []
  state.affiliationCount = 0
  state.accountantMembers = []
  state.booting = false
  bootPromise = null
})

async function bootstrap() {
  if (bootPromise) return bootPromise
  bootPromise = (async () => {
    syncAppClientVersion()
    const epoch = getSessionEpoch()
    try {
      const refresh = getRefreshToken()
      if (!refresh) {
        state.user = null
        return
      }
      // 인터셉터와 동일 mutex — refresh 토큰 이중 회전으로 세션이 날아가는 것 방지
      const { ok, data } = await refreshAccessToken()
      if (epoch !== getSessionEpoch()) return
      if (ok && data) {
        await applySession(data, epoch)
        if (epoch !== getSessionEpoch()) return
        // /me·푸시는 스플래시를 막지 않음 (느린 네트워크·API 지연 시 무한 로딩 방지)
        void (async () => {
          await refreshMe()
          if (epoch !== getSessionEpoch()) return
          void afterAuthSession()
        })()
      } else {
        clearLocalAuth()
      }
    } catch {
      if (epoch === getSessionEpoch()) clearLocalAuth()
    } finally {
      state.booting = false
      bootPromise = null
    }
  })()
  return bootPromise
}

async function loginCenter(loginId: string, loginPw: string, memberCode?: string) {
  try {
    const res = await appApi.post('/auth/login/center', {
      loginId,
      loginPw,
      ...(memberCode ? { memberCode } : {}),
    })
    if (!res.success) throw new Error(res.message || '로그인 실패')
    const data = res.data as Record<string, any>
    if (data.requireBizGubunSelect) return data
    clearSuperReturn()
    await applySession(data)
    await refreshMe()
    void afterAuthSession()
    return data
  } catch (e) {
    throw new Error(apiErrorMessage(e, '로그인 실패'))
  }
}

async function loginAccountant(loginId: string, loginPw: string, memberCode?: string) {
  try {
    const res = await appApi.post('/auth/login/accountant', {
      loginId,
      loginPw,
      ...(memberCode ? { memberCode } : {}),
    })
    if (!res.success) throw new Error(res.message || '로그인 실패')
    const data = res.data as Record<string, any>
    if (data.requireMemberSelect) return data
    clearSuperReturn()
    await applySession(data)
    await refreshMe()
    void afterAuthSession()
    return data
  } catch (e) {
    throw new Error(apiErrorMessage(e, '로그인 실패'))
  }
}

async function fetchAccountantMembers() {
  const res = await appApi.get('/accountant/members')
  if (!res.success) throw new Error(res.message || '기관 목록 조회 실패')
  const data = res.data as Record<string, any>
  state.accountantMembers = mapAccountantMembers(data?.members)
  return state.accountantMembers
}

async function switchAccountantMember(memberCode: string) {
  const res = await appApi.post('/accountant/switch-member', { memberCode })
  if (!res.success) throw new Error(res.message || '기관 변경 실패')
  await applySession(res.data as Record<string, any>)
  await refreshMe()
  return res.data
}

async function loginYobo(loginId: string, password: string) {
  try {
    const res = await appApi.post('/auth/login/yobo', {
      loginId,
      phone: loginId,
      email: loginId.includes('@') ? loginId : undefined,
      password,
    })
    if (!res.success) throw new Error(res.message || '로그인 실패')
    clearSuperReturn()
    await applySession(res.data as Record<string, any>)
    await refreshMe()
    void afterAuthSession()
  } catch (e) {
    throw new Error(apiErrorMessage(e, '로그인 실패'))
  }
}

async function enterAccount(payload: { kind: 'center' | 'yobo'; mCode?: string; cayIdx?: number }) {
  const created = !readSuperReturn()
  if (created) {
    const access = getAccessToken()
    const refresh = getRefreshToken()
    if (!access || !refresh) throw new Error('슈퍼 로그인 정보가 없습니다.')
    writeSuperReturn(access, refresh)
  }
  const post = (access: string) =>
    axios.post('/api/app/super/enter', payload, {
      timeout: 15000,
      headers: {
        Authorization: `Bearer ${access}`,
        'Content-Type': 'application/json',
      },
    })
  try {
    let token = readSuperReturn()?.access || ''
    let res
    try {
      res = await post(token)
    } catch (e) {
      if (axios.isAxiosError(e) && e.response?.status === 401) {
        const next = await refreshSavedSuper()
        if (!next) throw new Error('슈퍼 로그인이 만료되었습니다. 다시 로그인해 주세요.')
        res = await post(next)
      } else {
        throw e
      }
    }
    const body = res.data as Record<string, any>
    if (!body?.success) throw new Error(body?.message || '들어가지 못했습니다.')
    await applySession(body.data as Record<string, any>)
    await refreshMe()
    return state.user?.appRole || null
  } catch (e) {
    if (created) clearSuperReturn()
    throw new Error(apiErrorMessage(e, '들어가지 못했습니다.'))
  }
}

async function exitSuper() {
  const saved = readSuperReturn()
  if (!saved) throw new Error('돌아갈 슈퍼 계정이 없습니다.')
  saveTokens(saved.access, saved.refresh)
  clearSuperReturn()
  await refreshMe()
  if (!state.user) {
    const refreshed = await refreshAccessToken()
    if (refreshed.ok) await refreshMe()
  }
  if (!state.user?.isSuper) {
    throw new Error('슈퍼 계정으로 돌아오지 못했습니다. 다시 로그인해 주세요.')
  }
}

async function sendRegisterOtp(
  email: string,
  accountType: 'yobo' | 'center' = 'yobo'
): Promise<{ debugCode: string | null; mailed: boolean; cooldownSec: number; retryAfterSec?: number }> {
  try {
    const res = await appApi.post('/auth/otp/register', { email, accountType })
    if (!res.success) throw new Error(res.message || '발송 실패')
    const data = res.data as Record<string, any> | undefined
    return {
      debugCode: data?.debugCode != null ? String(data.debugCode) : null,
      mailed: !!data?.mailed,
      cooldownSec: Number(data?.cooldownSec) || 60,
    }
  } catch (e) {
    const err = Object.assign(new Error(apiErrorMessage(e, '발송 실패')), {
      retryAfterSec: apiRetryAfterSec(e),
    })
    throw err
  }
}

async function sendRegisterSmsOtp(
  phone: string
): Promise<{ debugCode: string | null; sent: boolean; cooldownSec: number }> {
  try {
    const res = await appApi.post('/auth/otp/register', { phone, accountType: 'yobo' })
    if (!res.success) throw new Error(res.message || '발송 실패')
    const data = res.data as Record<string, any> | undefined
    return {
      debugCode: data?.debugCode != null ? String(data.debugCode) : null,
      sent: !!data?.sent,
      cooldownSec: Number(data?.cooldownSec) || 60,
    }
  } catch (e) {
    const err = Object.assign(new Error(apiErrorMessage(e, '발송 실패')), {
      retryAfterSec: apiRetryAfterSec(e),
    })
    throw err
  }
}

async function peekRegisterOtp(
  email: string,
  code: string,
  accountType: 'yobo' | 'center' = 'yobo'
) {
  try {
    const res = await appApi.post('/auth/otp/register/verify', { email, code, accountType })
    if (!res.success) throw new Error(res.message || '인증 실패')
  } catch (e) {
    throw new Error(apiErrorMessage(e, '인증 실패'))
  }
}

async function peekRegisterSmsOtp(phone: string, code: string) {
  try {
    const res = await appApi.post('/auth/otp/register/verify', {
      phone,
      code,
      accountType: 'yobo',
    })
    if (!res.success) throw new Error(res.message || '인증 실패')
  } catch (e) {
    throw new Error(apiErrorMessage(e, '인증 실패'))
  }
}

async function registerYobo(payload: {
  phone: string
  code: string
  password: string
  name?: string
  agrees: { code: string; version: string }[]
  marketingAgree?: boolean
  pushAgree?: boolean
  emailAgree?: boolean
}) {
  try {
    const res = await appApi.post<{
      linkedCenters?: { centerName?: string; longtermCode?: string }[]
    }>('/auth/register', payload)
    if (!res.success) throw new Error(res.message || '가입 실패')
    await applySession(res.data as Record<string, any>)
    await refreshMe()
    return {
      linkedCenters: Array.isArray(res.data?.linkedCenters) ? res.data.linkedCenters : [],
    }
  } catch (e) {
    throw new Error(apiErrorMessage(e, '가입 실패'))
  }
}

async function registerCenter(payload: {
  email: string
  code: string
  loginId: string
  password: string
  centerName: string
  bossName: string
  phone: string
  addr: string
  bizGubun: string
  longtermCode: string
  agrees: { code: string; version: string }[]
  marketingAgree?: boolean
}) {
  try {
    const res = await appApi.post('/auth/register/center', payload)
    if (!res.success) throw new Error(res.message || '가입 실패')
    await applySession(res.data as Record<string, any>)
    await refreshMe()
  } catch (e) {
    throw new Error(apiErrorMessage(e, '가입 실패'))
  }
}

async function checkLoginId(loginId: string): Promise<boolean> {
  try {
    const res = await appApi.get('/auth/check-id', { loginId })
    if (!res.success) throw new Error(res.message || '중복 확인 실패')
    return !!(res.data as any)?.available
  } catch (e) {
    throw new Error(apiErrorMessage(e, '중복 확인 실패'))
  }
}

async function fetchBizGubuns() {
  const res = await appApi.get('/biz-gubun')
  if (!res.success) throw new Error(res.message || '사업구분 조회 실패')
  return (res.data as { short: string; group?: string }[]) || []
}

async function fetchTerms(opts?: { signal?: AbortSignal }) {
  const res = await appApi.get('/terms', undefined, opts)
  if (!res.success) throw new Error(res.message || '약관 조회 실패')
  return (res.data as Record<string, any>[]) || []
}

async function sendResetOtp(payload: {
  accountType: 'yobo' | 'center'
  phone?: string
  loginId?: string
}): Promise<{
  debugCode: string | null
  sent: boolean
  maskedPhone?: string
  cooldownSec: number
}> {
  try {
    const res = await appApi.post('/auth/otp/reset', payload)
    if (!res.success) throw new Error(res.message || '발송 실패')
    const data = res.data as Record<string, any> | undefined
    return {
      debugCode: data?.debugCode != null ? String(data.debugCode) : null,
      sent: !!data?.sent,
      maskedPhone: data?.maskedPhone ? String(data.maskedPhone) : undefined,
      cooldownSec: Number(data?.cooldownSec) || 60,
    }
  } catch (e) {
    const err = Object.assign(new Error(apiErrorMessage(e, '발송 실패')), {
      retryAfterSec: apiRetryAfterSec(e),
    })
    throw err
  }
}

async function peekResetOtp(payload: {
  accountType: 'yobo' | 'center'
  phone?: string
  loginId?: string
  code: string
}) {
  try {
    const res = await appApi.post('/auth/otp/reset/verify', payload)
    if (!res.success) throw new Error(res.message || '인증 실패')
  } catch (e) {
    throw new Error(apiErrorMessage(e, '인증 실패'))
  }
}

async function resetPassword(payload: {
  accountType: 'yobo' | 'center'
  phone?: string
  loginId?: string
  code: string
  password: string
  memberCode?: string
}) {
  try {
    const res = await appApi.post('/auth/reset-password', payload)
    if (!res.success) throw new Error(res.message || '변경 실패')
  } catch (e) {
    throw new Error(apiErrorMessage(e, '변경 실패'))
  }
}

async function logout() {
  try {
    sessionStorage.removeItem('caion_open_tab')
    sessionStorage.removeItem('caion_open_acct')
  } catch {
    /* ignore */
  }
  // 액세스·리프레시를 먼저 확보한 뒤 즉시 세션 제거
  // (푸시 해제 대기 중에 잔여 API가 401 → "내정보 인증실패" 알림이 뜨던 레이스 방지)
  const refresh = getRefreshToken()
  const access = getAccessToken()
  clearLocalAuth()
  try {
    notifyNativeLogout()
  } catch {
    /* ignore */
  }
  try {
    const { unregisterPushDevice } = await import('@/composables/useSchedulePush')
    await unregisterPushDevice(access)
  } catch {
    /* ignore */
  }
  if (refresh) {
    void axios
      .post(
        '/api/app/auth/logout',
        { refreshToken: refresh },
        { timeout: 8000, headers: { 'Content-Type': 'application/json' } }
      )
      .catch(() => {})
  }
}

function homeForRole(role?: string | null) {
  // fna 앱 — 회계담당·기관(센터장·사회복지사) 모두 영수증 홈
  if (
    isFnaFlavor() &&
    (role === 'ACCOUNT' || role === 'OWNER' || role === 'SOCIAL_WORKER')
  ) {
    return '/fna'
  }
  switch (role) {
    case 'OWNER':
      return '/owner'
    case 'SOCIAL_WORKER':
      return '/sw'
    case 'CAREGIVER':
      return '/caregiver'
    case 'ACCOUNT':
      return '/fna'
    default:
      return '/entry'
  }
}

export const authStore = {
  state,
  isLoggedIn: computed(() => !!state.user),
  role: computed(() => state.user?.appRole || null),
  isCenterOwner: computed(() => String(state.user?.appRole || '') === 'OWNER'),
  /** 센터장(시설 ACCOUNT) 또는 회계권한 부여된 사회복지사 · 웹 회계담당 */
  canUseAccounting: computed(() => {
    const u = state.user
    if (!u) return false
    const role = String(u.appRole || '')
    if (role === 'ACCOUNT') return true
    if (!u.hasAccountAssignee) return false
    if (role === 'OWNER') return true
    if (role === 'SOCIAL_WORKER') return u.canAccounting === true
    return false
  }),
  bootstrap,
  refreshMe,
  applyUserPatch,
  loginCenter,
  loginAccountant,
  fetchAccountantMembers,
  switchAccountantMember,
  loginYobo,
  enterAccount,
  exitSuper,
  impersonating,
  sendRegisterOtp,
  sendRegisterSmsOtp,
  peekRegisterOtp,
  peekRegisterSmsOtp,
  registerYobo,
  registerCenter,
  checkLoginId,
  fetchBizGubuns,
  fetchTerms,
  sendResetOtp,
  peekResetOtp,
  resetPassword,
  fetchProfile,
  updateProfile,
  changePassword,
  logout,
  homeForRole,
}
